import React from 'react';
import { buildBadges } from '../../domain/property/badges';
import { Badge } from '../../design-system/components/Badge';
import { Property, MapFilterChip } from '../../services/api';
import { FilterChip } from '../../design-system/components';
import { buildLoc, buildImg } from './mapHelpers';
import { formatUnits } from '../../domain/property/formatters';

interface SidebarPanelProps {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  setSearchFocused: (v: boolean) => void;
  searchSuggestions: Property[];
  showSearchDropdown: boolean;
  handleSuggestionSelect: (p: Property) => void;
  mapChips: MapFilterChip[];
  noChipActive: boolean;
  mapChipActiveIds: Set<number>;
  setMapChipActiveIds: (s: Set<number>) => void;
  toggleMapChip: (id: number) => void;
  sheetCards: Property[];
  favoriteIds: Set<number>;
  handleToggleFavorite: (e: React.MouseEvent, propertyId: number) => void;
  handleMarkerClick: (property: Property) => void;
  selectedProperty: Property | null;
}

// 데스크탑 좌측 패널 (lg+ 전용) — 검색·chip·세로 카드 리스트
export const SidebarPanel: React.FC<SidebarPanelProps> = ({
  searchQuery,
  setSearchQuery,
  setSearchFocused,
  searchSuggestions,
  showSearchDropdown,
  handleSuggestionSelect,
  mapChips,
  noChipActive,
  mapChipActiveIds,
  setMapChipActiveIds,
  toggleMapChip,
  sheetCards,
  favoriteIds,
  handleToggleFavorite,
  handleMarkerClick,
  selectedProperty,
}) => {
  return (
        <aside
          className="hidden lg:flex lg:absolute lg:left-0 lg:top-[54px] lg:bottom-0 lg:w-[404px] lg:flex-col lg:border-r lg:border-ca-line lg:bg-frame lg:overflow-hidden"
          aria-label="지도 영역 단지 패널"
        >
          {/* 검색바 — 사용자 피드백 2026-05-22: 실 검색 + 자동완성 (헤더와 동일 state 공유) */}
          <div className="relative border-b border-ca-line bg-frame px-4 py-3">
            <div className="flex h-10 items-center gap-2.5 rounded-ca-xl border border-transparent bg-ca-surface px-3 focus-within:border-ink focus-within:bg-frame">
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 180)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchSuggestions.length > 0) {
                    e.preventDefault();
                    handleSuggestionSelect(searchSuggestions[0]);
                  } else if (e.key === 'Escape') {
                    setSearchQuery('');
                    setSearchFocused(false);
                  }
                }}
                placeholder="단지명·동·지하철로 검색"
                className="flex-1 bg-transparent text-[13px] font-semibold text-ink placeholder:text-muted outline-none"
                aria-label="단지 검색"
              />
              {searchQuery && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); }}
                  className="text-mute2 hover:text-ink"
                  aria-label="검색어 지우기"
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {showSearchDropdown && (
              <div className="absolute left-4 right-4 top-full mt-1 max-h-[60vh] overflow-y-auto rounded-ca-md border border-ca-line bg-frame shadow-ca-2 z-30">
                {searchSuggestions.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleSuggestionSelect(p); }}
                    className="block w-full border-b border-ca-line-soft px-3 py-2 text-left last:border-0 hover:bg-ca-surface"
                  >
                    <div className="text-[13px] font-bold text-ink truncate">
                      {p.title}
                      {p.status === '분양완료' && <span className="ml-1.5 text-[11px] font-semibold text-muted">분양완료</span>}
                    </div>
                    <div className="mt-0.5 text-[11.5px] text-muted truncate">{p.address}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 카테고리 chip row — admin 에 활성 chip 이 1개 이상일 때만 노출 */}
          {mapChips.length > 0 && (
            <div className="border-b border-ca-line bg-frame">
              <div className="flex gap-1.5 overflow-x-auto px-4 py-2.5" style={{ scrollbarWidth: 'none' }}>
                <FilterChip
                  label="전체"
                  active={noChipActive}
                  onClick={() => setMapChipActiveIds(new Set())}
                />
                {mapChips.map((chip) => (
                  <FilterChip
                    key={chip.id}
                    label={chip.label}
                    active={mapChipActiveIds.has(chip.id)}
                    emphasis={chip.emphasis}
                    onClick={() => toggleMapChip(chip.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 헤더 — 영역 카운트 (사용자 피드백 2026-05-19: 한 줄, 지역명 생략).
              SEO: 단지 목록 섹션 제목(h2). 시각 텍스트는 카운트, 의미 키워드는 sr-only. */}
          <h2 className="flex items-baseline gap-1 bg-frame px-4 pb-1 pt-3 text-[13px] font-normal text-muted">
            <span className="sr-only">대구·경북 분양 단지 목록 — </span>
            이 지역
            <b className="num font-bold text-ink">{sheetCards.length.toLocaleString()}</b>
            개 단지
          </h2>

          {/* 세로 카드 리스트 — v2 `.lcard` (썸네일 104×88 · 배지 buildBadges: 대표혜택→할인율→분양예정) */}
          <div className="flex-1 overflow-y-auto bg-frame px-4 pb-4 pt-2">
            {sheetCards.length === 0 ? (
              <div className="px-4 py-10 text-center text-[12.5px] text-muted">
                이 영역에 표시할 단지가 없어요.
                <br />
                지도를 이동하거나 필터를 해제해보세요.
              </div>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {sheetCards.map((property) => {
                  const selected = selectedProperty?.id === property.id;
                  const badges = buildBadges(property as any);
                  const img = buildImg(property);
                  return (
                  <li key={property.id} className="relative">
                    {/* 찜(하트) — 행 버튼과 중첩 불가(HTML)라 형제로 absolute 배치 (2026-07-07) */}
                    <button
                      type="button"
                      onClick={(e) => handleToggleFavorite(e, property.id)}
                      aria-label={favoriteIds.has(property.id) ? '관심단지 해제' : '관심단지 등록'}
                      aria-pressed={favoriteIds.has(property.id)}
                      className="absolute right-2 top-2 z-[1] flex h-8 w-8 items-center justify-center rounded-full hover:bg-ca-surface active:translate-y-[1px]"
                    >
                      <svg width={16} height={16} viewBox="0 0 24 24"
                        fill={favoriteIds.has(property.id) ? 'var(--ca-accent)' : 'none'}
                        stroke={favoriteIds.has(property.id) ? 'var(--ca-accent)' : 'var(--ca-mute2)'}
                        strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMarkerClick(property)}
                      className={`flex w-full items-stretch gap-3 rounded-ca-3xl border bg-frame p-3 pr-10 text-left shadow-ca-card transition-colors ${
                        selected
                          ? 'border-accent shadow-[0_6px_18px_-8px_rgba(255,107,0,.5)]'
                          : 'border-transparent hover:border-ca-line'
                      }`}
                    >
                      <div className="h-[88px] w-[104px] shrink-0 overflow-hidden rounded-ca-xl bg-ca-surface">
                        {img && (
                          <img src={img} alt={property.title} loading="lazy" className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {badges.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {badges.slice(0, 2).map((b) => (
                              <Badge key={b.key} tone={b.tone} size="tag" ariaLabel={b.ariaLabel}>
                                {b.label}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <h4 className="text-[15px] font-semibold leading-[21px] tracking-tight text-ink line-clamp-1">
                          {property.title}
                        </h4>
                        <p className="text-[13px] leading-[18px] text-muted line-clamp-1">
                          {buildLoc(property)}
                          {formatUnits(property, { short: true }) ? ` · ${formatUnits(property, { short: true })}` : ''}
                        </p>
                        {typeof property.naver_search_volume === 'number' && property.naver_search_volume > 0 && (
                          <span className="num text-[11px] font-bold text-mute2">
                            검색량 {property.naver_search_volume.toLocaleString()}건
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* 좌측 패널 하단 — 1533-8124 안내 */}
          <div className="border-t border-ca-line bg-frame px-4 py-3 text-[11.5px] text-muted">
            궁금하신 점은 <b className="font-bold text-ink">1533-8124</b>로 문의해 주세요.
          </div>
        </aside>
  );
};
